import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Users, BedDouble, CalendarRange, TrendingUp, Building2, Sparkles, PlayCircle, ShieldCheck, Trees } from "lucide-react";
import { BrandHero } from "@/brand/BrandHero";

function Stat({ icon: Icon, label, value, hint, testid }) {
  return (
    <div
      data-testid={testid}
      className="stat-card bg-[#FFFFFF] border border-stone-200 rounded-lg p-6 flex flex-col justify-between min-h-[140px]"
    >
      <div className="flex items-start justify-between">
        <div className="w-10 h-10 rounded-md bg-[#EAF0EA] flex items-center justify-center text-[#083A6B]">
          <Icon size={18} strokeWidth={1.6} />
        </div>
        {hint && <div className="text-[10px] uppercase tracking-widest text-stone-400">{hint}</div>}
      </div>
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">{label}</div>
        <div className="font-serif text-base md:text-lg lg:text-xl text-stone-900 leading-tight tabular-nums whitespace-nowrap overflow-hidden text-ellipsis">{value}</div>
      </div>
    </div>
  );
}

const NUM_PT = ["Zero", "Uma", "Duas", "Três", "Quatro", "Cinco", "Seis", "Sete", "Oito", "Nove", "Dez", "Onze", "Doze", "Treze", "Catorze", "Quinze"];

export default function Dashboard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
  }, []);

  const tagline = useMemo(() => {
    const n = d?.total_pousadas ?? 0;
    if (n === 0) return "Um só sistema de gestão para toda a sua rede.";
    if (n === 1) return "Uma pousada, um só sistema de gestão.";
    const nome = NUM_PT[n] || String(n);
    return `${nome} pousadas, um só sistema de gestão.`;
  }, [d?.total_pousadas]);

  return (
    <div>
      <PageHeader
        eyebrow="Painel geral"
        title="Bem-vindo à sua rede de pousadas"
        description="Acompanhe hóspedes, receita e ocupação em todas as pousadas em um só lugar."
      />

      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-6 mb-10">
        <Stat
          testid="stat-hospedes"
          icon={Users}
          label="Total de hóspedes"
          value={d?.total_hospedes ?? "—"}
        />
        <Stat
          testid="stat-receita"
          icon={TrendingUp}
          label="Receita recebida"
          value={brl(d?.receita_recebida ?? d?.receita_total ?? 0)}
          hint="Pagamentos confirmados"
        />
        <Stat
          testid="stat-a-receber"
          icon={CalendarRange}
          label="A receber"
          value={brl(d?.receita_a_receber ?? 0)}
          hint="Saldo pendente"
        />
        <Stat
          testid="stat-ocupacao"
          icon={BedDouble}
          label="Ocupação"
          value={`${d?.ocupacao_percentual ?? 0}%`}
          hint={`${d?.quartos_ocupados ?? 0}/${d?.total_quartos ?? 0}`}
        />
        <Stat
          testid="stat-reservas"
          icon={CalendarRange}
          label="Reservas ativas"
          value={d?.reservas_ativas ?? "—"}
        />
      </section>

      {/* FASE 4A: Operação da Limpeza + Bloqueios da Chácara */}
      <section className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4A652] mb-3 font-semibold">Operação · Limpeza</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Link to="/admin/limpeza" data-testid="stat-aguardando-limpeza-link" className="block">
            <div className="stat-card bg-[#FFFFFF] border border-stone-200 rounded-lg p-6 flex flex-col justify-between min-h-[140px] hover:border-[#B4472B] hover:shadow-sm transition-all">
              <div className="w-10 h-10 rounded-md bg-[#FEE7DE] flex items-center justify-center text-[#B4472B]">
                <Sparkles size={18} strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Aguardando limpeza</div>
                <div className="font-serif text-xl md:text-2xl lg:text-3xl text-[#B4472B] leading-tight tabular-nums whitespace-nowrap">{d?.quartos_aguardando_limpeza ?? 0}</div>
              </div>
            </div>
          </Link>
          <Link to="/admin/limpeza" data-testid="stat-em-limpeza-link" className="block">
            <div className="stat-card bg-[#FFFFFF] border border-stone-200 rounded-lg p-6 flex flex-col justify-between min-h-[140px] hover:border-[#3671B0] hover:shadow-sm transition-all">
              <div className="w-10 h-10 rounded-md bg-[#DBE7F2] flex items-center justify-center text-[#1E4C7A]">
                <PlayCircle size={18} strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Em limpeza</div>
                <div className="font-serif text-xl md:text-2xl lg:text-3xl text-[#1E4C7A] leading-tight tabular-nums whitespace-nowrap">{d?.quartos_em_limpeza ?? 0}</div>
              </div>
            </div>
          </Link>
          <Link to="/admin/limpeza" data-testid="stat-aguardando-liberacao-link" className="block">
            <div className="stat-card bg-[#FFFFFF] border border-stone-200 rounded-lg p-6 flex flex-col justify-between min-h-[140px] hover:border-[#7B4FA1] hover:shadow-sm transition-all">
              <div className="w-10 h-10 rounded-md bg-[#E9DFEF] flex items-center justify-center text-[#5E3B7A]">
                <ShieldCheck size={18} strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Aguardando liberação</div>
                <div className="font-serif text-xl md:text-2xl lg:text-3xl text-[#5E3B7A] leading-tight tabular-nums whitespace-nowrap">{d?.quartos_aguardando_liberacao ?? 0}</div>
              </div>
            </div>
          </Link>
          <div data-testid="stat-bloqueados-chacara" className="stat-card bg-[#FFFFFF] border border-stone-200 rounded-lg p-6 flex flex-col justify-between min-h-[140px]">
            <div className="w-10 h-10 rounded-md bg-[#EAF0EA] flex items-center justify-center text-[#083A6B]">
              <Trees size={18} strokeWidth={1.6} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Bloqueados pela chácara</div>
              <div className="font-serif text-xl md:text-2xl lg:text-3xl text-[#083A6B] leading-tight tabular-nums whitespace-nowrap">{d?.quartos_bloqueados_chacara ?? 0}</div>
              <div className="text-[10px] text-stone-500 mt-1">Suítes indisponíveis por CI ativa hoje</div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#FFFFFF] border border-stone-200 rounded-lg overflow-hidden">
          <div className="relative h-56">
            <BrandHero testid="dashboard-brand-hero" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
            <div className="absolute bottom-0 left-0 p-8 text-white">
              <div className="text-[10px] uppercase tracking-[0.25em] mb-2 opacity-80">Serra da Mantiqueira</div>
              <div className="font-serif text-3xl md:text-4xl leading-tight max-w-md" data-testid="dashboard-tagline">
                {tagline}
              </div>
            </div>
          </div>
          <div className="p-8">
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Ocupação por pousada</div>
            <div className="space-y-4">
              {(d?.ocupacao_pousadas || []).map((p) => (
                <div key={p.pousada} className="flex items-center gap-4" data-testid={`ocupacao-${p.pousada}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-800 truncate">{p.pousada}</div>
                    <div className="text-xs text-stone-500">{p.ocupados}/{p.total_quartos} quartos ocupados</div>
                  </div>
                  <div className="w-32 md:w-48 h-2 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#083A6B]"
                      style={{ width: `${p.ocupacao}%` }}
                    />
                  </div>
                  <div className="w-10 text-right font-serif text-lg text-stone-800">{p.ocupacao}%</div>
                </div>
              ))}
              {(!d?.ocupacao_pousadas || d.ocupacao_pousadas.length === 0) && (
                <div className="text-sm text-stone-500">Cadastre quartos para ver a ocupação por pousada.</div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-8">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Status dos quartos</div>
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[#4A7C59]" />
                <span className="text-sm text-stone-700">Disponíveis</span>
              </div>
              <span className="font-serif text-2xl">{d?.quartos_disponiveis ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[#D4A652]" />
                <span className="text-sm text-stone-700">Ocupados</span>
              </div>
              <span className="font-serif text-2xl">{d?.quartos_ocupados ?? 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[#D99C3B]" />
                <span className="text-sm text-stone-700">Manutenção</span>
              </div>
              <span className="font-serif text-2xl">{d?.quartos_manutencao ?? 0}</span>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-stone-200">
            <div className="text-xs uppercase tracking-widest text-stone-500 mb-3">Rede</div>
            <div className="flex items-center gap-3">
              <Building2 size={18} className="text-[#083A6B]" />
              <div>
                <div className="font-serif text-3xl text-stone-900 leading-none whitespace-nowrap">{d?.total_pousadas ?? 0}</div>
                <div className="text-xs text-stone-500">pousadas cadastradas</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
